<!-- eslint-disable vue/html-closing-bracket-newline -->
<!-- eslint-disable vue/max-attributes-per-line -->
<!-- eslint-disable vue/first-attribute-linebreak -->
<!-- eslint-disable vue/html-indent -->
<template>
    <div ref="pdfContainerRef" class="pdf-container" :class="{ 'comment-mode-active': localCommentMode }"
        @click="onPdfClick">
        <canvas ref="pdfCanvas" />

        <div v-for="comment in visibleComments" :key="comment.id"
            class="comment-pin outline-dashed outline-red-500 bg-red-100 cursor-pointer bg-opacity-40"
            :style="{ top: `${comment.y}%`, left: `${comment.x}%` }" @click.stop="showCommentDetails(comment)">
            <div
                class="absolute -top-7 rounded bg-base-content text-base-100 px-2 text-sm whitespace-nowrap pointer-events-none">
                Comment
            </div>
            <IconMessage />
            <span v-if="(comment.replies?.length || 0) > 0" class="comment-count">
                {{ comment.replies.length }}
            </span>
        </div>

        <CommentPopup v-if="activeComment && !activeComment.parent_id && !commentsLocked" :x="activeComment?.x"
            :y="activeComment?.y" :submitters="submitters" :editing-comment="editingComment"
            :parent-id="activeComment?.parent_id" :page-number="activeComment?.page" @close="closeCommentPopup"
            @submit="submitComment" @delete="deleteComment" />
    </div>

    <Teleport to="body">
        <div v-if="selectedComment" class="comment-details"
            :style="{ position: 'fixed', top: detailsPosition.top + 'px', left: detailsPosition.left + 'px', transform: 'translate(-50%, 8px)', zIndex: 99999 }">
            <div class="comment-header">
                <span class="comment-author text-sm">{{ selectedComment.user.first_name }} {{
                    selectedComment.user.last_name }}</span>
                <span class="comment-time text-xs">{{ formatTime(selectedComment.created_at) }}</span>
                <button v-if="canEditComment(selectedComment)" class="!text-cs-main-500 edit-btn btn-sm"
                    @click="editComment(selectedComment)">
                    Edit
                </button>
            </div>
            <div class="comment-content text-sm">
                {{ selectedComment.content }}
            </div>
            <div v-if="selectedComment.mentioned_users?.length" class="comment-mentions">
                <span class="mention-label">Mentioned:</span>
                <span v-for="user in selectedComment.mentioned_users" :key="user.id" class="mention-tag">
                    {{ user.full_name }}
                </span>
            </div>
            <div v-if="selectedComment.replies?.length" class="comment-replies">
                <div v-for="reply in selectedComment.replies" :key="reply.id" class="reply">
                    <div class="reply-header">
                        <span class="reply-author text-sm">{{ reply.user.first_name }} {{ reply.user.last_name }}</span>
                        <span class="reply-time text-xs">{{ formatTime(reply.created_at) }}</span>
                    </div>
                    <div class="reply-content text-xs">
                        {{ reply.content }}
                    </div>
                    <div v-if="reply.mentioned_users?.length" class="comment-mentions">
                        <span class="mention-label">Mentioned:</span>
                        <span v-for="user in reply.mentioned_users" :key="user.id" class="mention-tag">
                            {{ user.full_name }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Inline Reply Form -->
            <div v-if="selectedComment && replyingToCommentId === selectedComment.id" class="inline-reply-form">
                <textarea v-model="inlineReplyContent" placeholder="Write a reply..." rows="3"
                    class="inline-reply-textarea" @input="handleMentionInput" />
                <div class="mentions-container">
                    <div class="selected-mentions">
                        <span v-for="mention in selectedMentions" :key="mention.id" class="mention-tag">
                            {{ mention.full_name ? mention.full_name : mention.email }}
                            <button @click="removeMention(mention)">×</button>
                        </span>
                    </div>

                    <div v-if="showMentionSuggestions" class="mention-suggestions">
                        <div v-for="user in filteredUsers" :key="user.email" class="mention-suggestion"
                            @click="addMention(user)">
                            {{ user.full_name ? user.full_name : user.email }}
                        </div>
                    </div>
                </div>
                <div class="inline-reply-actions">
                    <button class="button-cs-primary-sm !normal-case" @click="submitInlineReply">
                        Submit Reply
                    </button>
                    <button class="button-cs-outline-primary-sm !border !normal-case" @click="cancelInlineReply">
                        Cancel
                    </button>
                </div>
            </div>

            <div class="comment-actions">
                <button
                    v-if="!(selectedComment && replyingToCommentId === selectedComment.id) && !isCurrentUserBcc() && !commentsLocked"
                    class="button-cs-primary-sm !normal-case" @click="replyToComment(selectedComment)">
                    Reply
                </button>
                <button class="button-cs-outline-primary-sm !border !normal-case"
                    @click="selectedComment = null; cancelInlineReply();">
                    Close
                </button>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import CommentPopup from './new_comment_popup.vue'
import { formatDistanceToNow } from 'date-fns'
import { IconMessage } from '@tabler/icons-vue'

const comments = ref([])
const activeComment = ref(null)
const selectedComment = ref(null)
const editingComment = ref(null)
const pollingInterval = ref(null)
const pdfContainerRef = ref(null)
const detailsPosition = ref({ top: 0, left: 0 })

const localCommentMode = ref(false)

// New refs for inline reply
const replyingToCommentId = ref(null)
const inlineReplyContent = ref('')
const selectedMentions = ref([])
const showMentionSuggestions = ref(false)
const mentionSearch = ref('')

const props = defineProps(['documentUuid', 'pageIndex', 'submissionSlug', 'currentUserId', 'submitters', 'commentsLocked'])

const commentsLocked = computed(() => props.commentsLocked === true || props.commentsLocked === 'true')

function isCurrentUserBcc() {
    try {
        const submitters = JSON.parse(props.submitters)
        const user = submitters.find(u => u.id === props.currentUserId)
        return user && user.preferences && (user.preferences.is_bcc_contact === 'true' || user.preferences.is_real_bcc_contact === 'true')
    } catch (e) { return false }
}

const handleToggleCommentMode = () => {
    // Enter comment mode whenever the event is received
    activeComment.value = null
    selectedComment.value = null
    editingComment.value = null
    localCommentMode.value = true
}

const visibleComments = computed(() => {
    return comments.value.filter(comment => String(comment.page_number) === String(props.pageIndex) && !comment.parent_id)
})

const onPdfClick = (event) => {
    if (commentsLocked.value) {
        return
    }

    // Fallback: if the global cursor is in comment mode (crosshair),
    // treat this click as a comment placement even if local state
    // did not toggle yet.
    if (!localCommentMode.value) {
        if (document.body.style.cursor === 'crosshair') {
            localCommentMode.value = true
        } else {
            return
        }
    }

    const rect = event.currentTarget.getBoundingClientRect()
    const xPercent = ((event.clientX - rect.left) / rect.width) * 100
    const yPercent = ((event.clientY - rect.top) / rect.height) * 100

    activeComment.value = {
        x: xPercent,
        y: yPercent,
        page: props.pageIndex
    }

    localCommentMode.value = false

    // Reset cursor back to default
    document.body.style.cursor = 'default'
}

const showCommentDetails = (comment, event) => {
    selectedComment.value = comment
    nextTick(() => {
        if (pdfContainerRef.value) {
            const containerRect = pdfContainerRef.value.getBoundingClientRect()
            const x = containerRect.left + (comment.x / 100) * containerRect.width
            const y = containerRect.top + (comment.y / 100) * containerRect.height
            // Keep popup on screen horizontally
            const clampedLeft = Math.min(Math.max(x, 160), window.innerWidth - 160)
            detailsPosition.value = { top: y, left: clampedLeft }
        }
    })
}

const closeCommentPopup = () => {
    activeComment.value = null
    editingComment.value = null
}

const editComment = (comment) => {
    editingComment.value = comment
    activeComment.value = {
        x: comment.x,
        y: comment.y,
        page: comment.page_number
    }
    replyingToCommentId.value = null // Ensure inline reply is not active when editing
}

const replyToComment = (comment) => {
    if (commentsLocked.value || isCurrentUserBcc()) {
        return
    }

    if (replyingToCommentId.value === comment.id) {
        // If already replying to this comment and "Reply" is clicked again (though button might be hidden),
        // or if called programmatically, treat as cancel.
        cancelInlineReply()
    } else {
        selectedComment.value = comment // Ensure the correct comment's details are shown
        replyingToCommentId.value = comment.id
        inlineReplyContent.value = '' // Clear any previous content
        activeComment.value = null // Ensure main popup for new comments is hidden
        editingComment.value = null // Ensure not in edit mode
        // TODO: Consider nextTick to focus the textarea if it's immediately visible
    }
}

const cancelInlineReply = () => {
    replyingToCommentId.value = null
    inlineReplyContent.value = ''
}

const canEditComment = (comment) => {
    return comment.user.id === props.currentUserId
}

const formatTime = (timestamp) => {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true })
}

async function submitComment(data) {
    if (commentsLocked.value) {
        return
    }

    const method = data.id ? 'PUT' : 'POST'
    const url = data.id
        ? `/submissions/${props.submissionSlug}/comments/${data.id}`
        : `/submissions/${props.submissionSlug}/comments`

    await fetch(url, {
        method,
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ comment: data })
    })

    closeCommentPopup()
    await loadComments()
}

async function deleteComment(commentId) {
    if (!confirm('Are you sure you want to delete this comment?')) return

    await fetch(`/submissions/${props.submissionSlug}/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
    })

    closeCommentPopup()
    await loadComments()
}

async function loadComments() {
    if (!props.submissionSlug) {
        console.warn('Submission slug is not available')
        return
    }

    try {
        const res = await fetch(`/submissions/${props.submissionSlug}/comments`, {
            headers: {
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
            }
        })
        if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`)
        }
        comments.value = await res.json()
    } catch (error) {
        console.error('Error loading comments:', error)
    }
}

const startPolling = () => {
    if (!props.submissionSlug) {
        console.warn('Submission slug is not available, polling not started')
        return
    }
    pollingInterval.value = setInterval(loadComments, 5000)
}

// New function to submit inline reply
async function submitInlineReply() {
    if (commentsLocked.value) return
    if (!inlineReplyContent.value.trim()) return

    if (!selectedComment.value) {
        console.error('Cannot submit reply without a selected comment.')
        return
    }

    const commentData = {
        content: inlineReplyContent.value,
        page_number: selectedComment.value.page_number,
        x: selectedComment.value.x,
        y: selectedComment.value.y,
        parent_id: selectedComment.value.id,
        mentioned_user_ids: selectedMentions.value.map(m => m.id),
        visibility: 'all_submitters' // Default visibility, adjust as needed
        // mentioned_users_ids: [] // Add if @mention is supported in inline replies
    }

    try {
        await fetch(`/submissions/${props.submissionSlug}/comments`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
            },
            body: JSON.stringify({ comment: commentData })
        })

        const parentCommentId = selectedComment.value.id // Store the ID of the parent comment
        cancelInlineReply() // Clear content and hide form
        await loadComments() // Reload all comments

        // After loading all comments, find the updated parent comment in the new comments.value list
        // and update selectedComment.value to this new object reference.
        // This ensures the UI reacts to the new reply.
        const updatedParentComment = comments.value.find(comment => comment.id === parentCommentId)
        if (updatedParentComment) {
            selectedComment.value = updatedParentComment
        } else {
            // This case should ideally not happen if the parent comment still exists.
            // If it can be deleted, selectedComment might need to be cleared.
            selectedComment.value = null
        }

        // To make the UI feel more responsive, find the parent comment in comments.value
        // and optimistically update its replies array or replies_count.
        // For now, loadComments will refresh everything.
    } catch (error) {
        console.error('Error submitting inline reply:', error)
        // TODO: Display error to the user
    }
}

const handleShowCommentDetail = (event) => {
    const { commentId, pageNumber } = event.detail
    if (String(pageNumber) !== String(props.pageIndex)) return

    const comment = comments.value.find(c => c.id === commentId)
    if (comment) {
        selectedComment.value = comment
        activeComment.value = null
        editingComment.value = null
    }
}

onMounted(() => {
    // Listen for the global event dispatched by Stimulus
    window.addEventListener('toggle-comment-mode', handleToggleCommentMode)
    window.addEventListener('show-comment-detail', handleShowCommentDetail)

    if (props.submissionSlug) {
        loadComments()
        startPolling()
    }
})

onUnmounted(() => {
    // Clean up the event listener
    window.removeEventListener('toggle-comment-mode', handleToggleCommentMode)
    window.removeEventListener('show-comment-detail', handleShowCommentDetail)

    if (pollingInterval.value) {
        clearInterval(pollingInterval.value)
    }
})

const filteredUsers = computed(() => {
    if (!mentionSearch.value) return []
    const search = mentionSearch.value.toLowerCase()
    const listSubmitters = JSON.parse(props.submitters)
    return listSubmitters.filter(user =>
        (user.email.toLowerCase().includes(search) || user.full_name.toLowerCase().includes(search)) &&
        !selectedMentions.value.find(m => m.id === user.id)
    )
})

const handleMentionInput = (event) => {
    const text = event.target.value
    const lastWord = text.split(' ').pop()

    if (lastWord.startsWith('@')) {
        mentionSearch.value = lastWord.slice(1)
        showMentionSuggestions.value = true
    } else {
        showMentionSuggestions.value = false
    }
}

const addMention = (user) => {
    const textarea = document.querySelector('.inline-reply-textarea')

    if (!textarea) return

    const cursorPos = textarea.selectionStart
    const value = textarea.value

    // Find the word just before the cursor (starting with @)
    const beforeCursor = value.slice(0, cursorPos)
    const afterCursor = value.slice(cursorPos)

    const words = beforeCursor.split(/\s+/)
    const lastWord = words.pop()

    if (lastWord && lastWord.startsWith('@')) {
        const newBefore = words.join(' ')
        const newText = `${newBefore} ${user.full_name} ${afterCursor}`.trim()

        textarea.value = newText
        inlineReplyContent.value = newText

        // Move cursor to after the inserted name
        const newCursorPos = (newBefore + ' ' + user.full_name + ' ').length
        textarea.setSelectionRange(newCursorPos, newCursorPos)
    }

    selectedMentions.value.push(user)
    showMentionSuggestions.value = false
    mentionSearch.value = ''
}

const removeMention = (mention) => {
    selectedMentions.value = selectedMentions.value.filter(m => m.id !== mention.id)
}
</script>

<style scoped>
.pdf-container {
    position: relative;
    width: 100%;
    height: 100%;
}

.comment-pin {
    position: absolute;
    transform: translate(-50%, -50%);
    font-size: 16px;
    cursor: pointer;
    transition: transform 0.2s;
}

.comment-pin:hover {
    transform: translate(-50%, -50%) scale(1.2);
}

.comment-count {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #1976d2;
    color: white;
    font-size: 12px;
    padding: 2px 6px;
    border-radius: 10px;
    min-width: 16px;
    text-align: center;
}

.comment-details {
    position: absolute;
    transform: translate(-50%, -50%);
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
    width: 300px;
    z-index: 99999;
    padding: 16px;

    @media (max-width: 640px) {
        scale: 0.7;
    }
}

.comment-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.comment-author {
    font-weight: 600;
}

.comment-time {
    color: #666;
    font-size: 12px;
}

.edit-btn {
    margin-left: auto;
    background: none;
    border: none;
    color: #1976d2;
    cursor: pointer;
    padding: 4px 8px;
}

.comment-content {
    margin-bottom: 12px;
    line-height: 1.4;
}

.comment-mentions {
    margin-bottom: 12px;
}

.mention-label {
    color: #666;
    font-size: 12px;
    margin-right: 4px;
}

.mention-tag {
    background: #e3f2fd;
    color: #1976d2;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    margin-right: 4px;
}

.comment-replies {
    margin-bottom: 12px;
    padding-top: 12px;
    border-top: 1px solid #eee;
}

.reply {
    margin-bottom: 8px;
}

.reply-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.reply-author {
    font-weight: 600;
    font-size: 14px;
}

.reply-time {
    color: #666;
    font-size: 12px;
}

.reply-content {
    font-size: 14px;
    line-height: 1.4;
}

.comment-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
}

.reply-btn,
.close-btn {
    padding: 6px 16px;
    border-radius: 4px;
    cursor: pointer;
}

.reply-btn {
    background: #1976d2;
    color: white;
    border: none;
}

.close-btn {
    background: white;
    border: 1px solid #ddd;
}

/* Styles for Inline Reply Form */
.inline-reply-form {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #eee;
}

.inline-reply-textarea {
    width: 100%;
    min-height: 60px;
    padding: 8px;
    border: 1px solid #B1B0E8;
    border-radius: 4px;
    margin-bottom: 12px;
    font-size: 14px;
    resize: vertical;
}

.inline-reply-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.inline-reply-submit-btn,
.inline-reply-cancel-btn {
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
}

.inline-reply-submit-btn {
    background: #1976d2;
    color: white;
    border: none;
}

.inline-reply-cancel-btn {
    background: white;
    border: 1px solid #ddd;
    color: #333;
}

.mentions-container {
    position: relative;
    margin-bottom: 12px;
}

.selected-mentions {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 8px;
}

.mention-tag {
    background: #e3f2fd;
    color: #1976d2;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.mention-tag button {
    background: none;
    border: none;
    color: #1976d2;
    cursor: pointer;
    padding: 0 2px;
}

.mention-suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 21;
}

.mention-suggestion {
    padding: 8px 12px;
    cursor: pointer;
}

.mention-suggestion:hover {
    background: #f5f5f5;
}
</style>
